import React, { useState, useCallback, useEffect } from "react";
import clsx from "clsx";
import moment from "moment";
import { ReactComponent as CalenderAdd } from "Assets/icon/calender-add-icon.svg";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./date-time-picker.module.scss";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import { Button, IconButton } from "components/ui";
import { ReactComponent as TrashIcon } from "Assets/icon/trash-icon.svg";
import { trackEvent } from "utility/mixpanel";
import { useInViews } from "utility/useInViews";
import { useToast } from "components/ui";

interface DateTimePickerProps {
  isDateTimeSelected: boolean;
  selectedDateTime?: string;
  onDateTimeSelect: (dateTime: string) => void;
  onDateTimeDelete: () => void;
  title?: string;
  description?: string;
  isAddDisabled?: boolean;
  isDeleteDisabled?: boolean;
  className?: string;
  isAddingLoading?: boolean;
  isDeletingLoading?: boolean;
  minDate?: string;
  maxDate?: string;
  endDateTime?: string;
  defaultDateTime?: string;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  isDateTimeSelected,
  selectedDateTime,
  onDateTimeSelect,
  onDateTimeDelete,
  title,
  description,
  isAddDisabled = false,
  isDeleteDisabled = false,
  className,
  isAddingLoading = false,
  isDeletingLoading = false,
  minDate,
  maxDate,
  endDateTime,
  defaultDateTime,
}) => {
  const [showCalendarButtons, setShowCalendarButtons] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    if (defaultDateTime) {
      return moment(defaultDateTime).local().format("YYYY-MM-DD");
    }
    return "";
  });
  const [selectedTime, setSelectedTime] = useState<string>(() => {
    if (defaultDateTime) {
      return moment(defaultDateTime).local().format("HH:mm");
    }
    return "";
  });
  const { ref, inView } = useInViews();
  const { openToast } = useToast();
  const { t } = useTranslation(["components", "common"]);

  const logImpression = useCallback(() => {
    trackEvent("DateTimePicker Impression", {});
  }, []);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  const parsedStartDateTime = selectedDateTime
    ? moment.utc(selectedDateTime)
    : null;
  const parsedEndDateTime = endDateTime ? moment.utc(endDateTime) : null;

  const displayDate = parsedStartDateTime
    ? parsedStartDateTime.local().format("dddd Do MMMM")
    : "";

  const displayTime =
    parsedStartDateTime && parsedEndDateTime
      ? (() => {
          const localDateTime = parsedStartDateTime.local();
          const startTime = localDateTime.format("HH:mm");
          const endTime = parsedEndDateTime.local().format("HH:mm");

          return `${startTime}-${endTime}`;
        })()
      : parsedStartDateTime
      ? parsedStartDateTime.local().format("HH:mm")
      : "";

  const nowLocal = moment();
  const formatMinDate = minDate
    ? moment.utc(minDate).local().format("YYYY-MM-DD")
    : nowLocal.format("YYYY-MM-DD");
  const formatMaxDate = maxDate
    ? moment.utc(maxDate).local().format("YYYY-MM-DD")
    : undefined;

  const formatMinTime = minDate
    ? moment.utc(minDate).local().format("HH:mm")
    : nowLocal.format("HH:mm");
  const formatMaxTime = maxDate
    ? moment.utc(maxDate).local().format("HH:mm")
    : undefined;

  const onSaveHandler = () => {
    const nowLocal = moment();
    const minDateTime = minDate ? moment.utc(minDate).local() : nowLocal;
    const maxDateTime = maxDate ? moment.utc(maxDate).local() : undefined;

    const localDateTime = moment(`${selectedDate}T${selectedTime}`);
    if (
      (minDateTime &&
        (localDateTime.isBefore(minDateTime) ||
          localDateTime.isSame(minDateTime))) ||
      (maxDateTime && localDateTime.isAfter(maxDateTime))
    ) {
      trackEvent("DateTimePicker Save Blocked, Out of Range");
      openToast({
        message: t("components:dateTimePicker.invalidTimeMessage"),
        size: "medium",
      });
      return;
    }

    const utcDateTime = localDateTime.utc().format();
    onDateTimeSelect(utcDateTime);
  };

  const onCancelHandler = () => {
    trackEvent("Date Time Picker Cancel Clicked");
    setSelectedDate("");
    setSelectedTime("");
    setShowCalendarButtons(!showCalendarButtons);
  };

  return (
    <div className={className} ref={ref}>
      {isDateTimeSelected ? (
        <div className={styles["dateSelectedContainer"]}>
          <div className={styles["selectedInfo"]}>
            <div className={styles["selectedTitle"]}>
              <p>{t("components:dateTimePicker.scheduled")}</p>
            </div>
            <div className={styles["selectedDateTime"]}>
              <p className={styles["selectedTime"]}>{displayTime}</p>
              <p className={styles["selectedDate"]}>{displayDate}</p>
            </div>
          </div>
          <IconButton
            icon={<TrashIcon stroke="black" />}
            shape="square"
            loading={isDeletingLoading}
            onClick={onDateTimeDelete}
            aria-label={t("components:dateTimePicker.removeA11y")}
            className={styles["trash-button"]}
            variant="enclosed-primary"
            monochromeColour="white"
            disabled={isDeleteDisabled}
          />
        </div>
      ) : (
        <div className={styles["dateNotSelectedContainer"]}>
          <div className={styles["addToCalendarButton"]}>
            <CalenderAdd className={styles["calender-icon"]} />
            <Button
              variant="primary"
              className={styles["calendarOpenButton"]}
              onClick={() => {
                trackEvent("DateTimePicker Calendar Toggle Clicked");
                setShowCalendarButtons(!showCalendarButtons);
              }}
              icon={
                <ChevronIcon
                  className={
                    showCalendarButtons
                      ? styles["chevron-icon-open"]
                      : styles["chevron-icon-close"]
                  }
                />
              }
              iconPosition="end"
              contentClassName={styles["calendarOpenButtonContent"]}
              disabled={isAddDisabled}
            >
              {title}
            </Button>
          </div>
          {showCalendarButtons && (
            <div className={styles["calendarButtonContent"]}>
              <p className={styles["calendarButtonDescription"]}>
                {description}
              </p>
              <div className={styles["calendarButtonDropdowns"]}>
                <input
                  type="datetime-local"
                  value={
                    selectedDate && selectedTime
                      ? `${selectedDate}T${selectedTime}`
                      : ""
                  }
                  min={
                    formatMinDate && formatMinTime
                      ? `${formatMinDate}T${formatMinTime}`
                      : ""
                  }
                  max={
                    formatMaxDate && formatMaxTime
                      ? `${formatMaxDate}T${formatMaxTime}`
                      : ""
                  }
                  onChange={(e) => {
                    trackEvent("Date-Time Dropdown Clicked");
                    const [date, time] = e.target.value.split("T");
                    setSelectedDate(date);
                    setSelectedTime(time);
                  }}
                  className={clsx(
                    styles["datetimeInput"],
                    selectedDate &&
                      selectedTime &&
                      styles["dateTimeInputSelected"]
                  )}
                />
              </div>
              <div className={styles["saveCancelContainer"]}>
                <Button
                  variant="enclosed-primary"
                  onClick={onSaveHandler}
                  disabled={!selectedDate || !selectedTime || isAddDisabled}
                  loading={isAddingLoading}
                >
                  {t("common:action.save")}
                </Button>
                <Button
                  variant="ghost"
                  className={styles["cancelButton"]}
                  onClick={onCancelHandler}
                >
                  {t("common:action.cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
